import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff, UserPlus, Phone, Gift, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import PhoneInput from '../../components/common/PhoneInput';
import { Card, CardContent } from '../../components/ui/Card';
import { cn } from '../../utils/cn';
import { useAuth } from '../../contexts/AuthContext';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { referralAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const EXAM_TYPES = ['WAEC', 'JAMB', 'POST_UME'];
const SUBJECTS = ['English', 'Mathematics', 'Physics', 'Chemistry', 'Biology'];

// Validation schema
const validationSchema = yup.object({
  firstName: yup.string()
    .min(2, 'First name must be at least 2 characters')
    .required('First name is required'),
  lastName: yup.string()
    .min(2, 'Last name must be at least 2 characters')
    .required('Last name is required'),
  email: yup.string()
    .email('Please enter a valid email address')
    .optional(),
  phone: yup.string()
    .matches(/^\+?[\d\s-]{10,20}$/, 'Please enter a valid phone number (with country code)')
    .optional(),
  password: yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain uppercase, lowercase, and number')
    .required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password'),
  role: yup.string()
    .oneOf(['student', 'teacher', 'parent'], 'Please select a valid role')
    .required('Role is required'),
  referralCode: yup.string()
    .min(6, 'Referral code must be at least 6 characters')
    .max(10, 'Referral code must be at most 10 characters')
    .optional(),
  agreeToTerms: yup.boolean()
    .oneOf([true], 'You must agree to the terms and conditions')
    .required('You must agree to the terms and conditions')
}).test('email-or-phone-required', 'Either email or phone is required', (values) => {
  if (!values) return false;
  const hasEmail = !!(values.email && values.email.trim());
  const hasPhone = !!(values.phone && values.phone.trim());
  return hasEmail || hasPhone;
}).shape({
  // Student-specific validation
  dateOfBirth: yup.string()
    .when('role', {
      is: 'student',
      then: (schema) => schema.required('Date of birth is required').matches(/^\d{4}-\d{2}-\d{2}$/,
        'Please provide a valid date (YYYY-MM-DD)')
    }),
  examType: yup.array()
    .of(yup.string().oneOf(EXAM_TYPES, 'Invalid exam type'))
    .when('role', {
      is: 'student',
      then: (schema) => schema.min(1, 'Select at least one exam type').required('Exam type is required')
    }),
  subjects: yup.array()
    .of(yup.string().oneOf(SUBJECTS, 'Invalid subject'))
    .when('role', {
      is: 'student',
      then: (schema) => schema.min(1, 'Select at least one subject').required('Subjects are required')
    }),
  // Teacher-specific validation
  qualifications: yup.string()
    .when('role', {
      is: 'teacher',
      then: (schema) => schema.required('Qualifications are required')
    }),
  specialization: yup.array()
    .of(yup.string().oneOf(SUBJECTS, 'Invalid specialization subject'))
    .when('role', {
      is: 'teacher',
      then: (schema) => schema.min(1, 'Select at least one specialization').required('Specialization is required')
    }),
  experience: yup.number()
    .typeError('Experience must be a number')
    .integer('Experience must be an integer')
    .min(0, 'Experience cannot be negative')
    .when('role', {
      is: 'teacher',
      then: (schema) => schema.required('Experience is required')
    })
});

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState(null);
  const [referralValidationStatus, setReferralValidationStatus] = useState('idle'); // idle | validating | valid | invalid
  const [referralError, setReferralError] = useState('');
  
  const { register } = useAuth();
  const { showError, showSuccess } = useNotification();
  const { appName } = useAppConfig();
  const navigate = useNavigate();

  const roles = [
    { value: 'student', label: 'Student' },
    { value: 'teacher', label: 'Teacher' },
    { value: 'parent', label: 'Parent' }
  ];

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'student',
      referralCode: '',
      agreeToTerms: false,
      // Student fields
      dateOfBirth: '',
      examType: [],
      subjects: [],
      // Teacher fields
      qualifications: '', // comma-separated
      specialization: [],
      experience: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phoneNumber: values.phone,
          password: values.password,
          role: values.role,
          referralCode: values.referralCode.trim() || undefined,
          source: 'web'
        };
        if (values.role === 'student') {
          payload.dateOfBirth = values.dateOfBirth; // YYYY-MM-DD
          payload.examType = values.examType;
          payload.subjects = values.subjects;
        }
        if (values.role === 'teacher') {
          payload.qualifications = values.qualifications
            .split(',')
            .map(q => q.trim())
            .filter(q => q);
          payload.specialization = values.specialization;
          payload.experience = Number(values.experience);
        }

        const result = await register(payload);
        if (result?.success) {
          showSuccess('Registration successful! Welcome to your dashboard.');
          navigate('/dashboard');
        } else {
          const msg = result?.error || 'Registration failed. Please check your details and try again.';
          showError(msg);
        }
      } catch (error) {
        const msg = error?.response?.data?.message || error.message || 'Registration failed. Please try again.';
        showError(msg);
      } finally {
        setLoading(false);
      }
    }
  });

  // Read referral code from URL (?ref=CODE) and prefill the form
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      formik.setFieldValue('referralCode', ref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Validate referral code when it changes (debounced on blur/change)
  useEffect(() => {
    const code = (formik.values.referralCode || '').trim();
    if (!code || code.length < 6) {
      setReferrerInfo(null);
      setReferralValidationStatus('idle');
      setReferralError('');
      return;
    }

    let cancelled = false;
    const validate = async () => {
      try {
        setReferralValidationStatus('validating');
        setReferralError('');
        const resp = await referralAPI.validateCode(code);
        const success = resp?.data?.success ?? resp?.success;
        const data = resp?.data?.data ?? resp?.data;
        if (!cancelled && success && data) {
          setReferrerInfo({
            name: data.referrer?.name || `${data.referrer?.firstName || ''} ${data.referrer?.lastName || ''}`.trim(),
            userId: data.referrer?._id,
            code: data.code || code,
          });
          setReferralValidationStatus('valid');
        } else if (!cancelled) {
          setReferrerInfo(null);
          setReferralValidationStatus('invalid');
          setReferralError(resp?.data?.message || 'Invalid referral code');
        }
      } catch (err) {
        if (!cancelled) {
          setReferrerInfo(null);
          setReferralValidationStatus('invalid');
          const msg = err?.response?.data?.message || err?.message || 'Invalid referral code';
          setReferralError(msg);
        }
      }
    };

    // Trigger validation after small delay to avoid spamming
    const timer = setTimeout(validate, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.referralCode]);

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];
    
    return {
      strength,
      label: labels[strength - 1] || '',
      color: colors[strength - 1] || 'bg-gray-300'
    };
  };

  const passwordStrength = getPasswordStrength(formik.values.password);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create Account</h1>
          <p className="text-gray-600">Join {appName} and start your learning journey</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardContent className="p-8">
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              {/* Name Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  name="firstName"
                  type="text"
                  placeholder="First name"
                  icon={User}
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && formik.errors.firstName}
                  autoComplete="given-name"
                />
                <Input
                  label="Last Name"
                  name="lastName"
                  type="text"
                  placeholder="Last name"
                  icon={User}
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && formik.errors.lastName}
                  autoComplete="family-name"
                />
              </div>

              {/* Student Fields */}
              {formik.values.role === 'student' && (
                <div className="space-y-4">
                  <Input
                    label="Date of Birth"
                    name="dateOfBirth"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    value={formik.values.dateOfBirth}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.dateOfBirth && formik.errors.dateOfBirth}
                  />

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Exam Type</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {EXAM_TYPES.map(type => (
                        <label key={type} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formik.values.examType.includes(type)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const current = formik.values.examType;
                              const next = checked ? [...current, type] : current.filter(t => t !== type);
                              formik.setFieldValue('examType', next);
                            }}
                          />
                          <span>{type}</span>
                        </label>
                      ))}
                    </div>
                    {formik.touched.examType && formik.errors.examType && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {typeof formik.errors.examType === 'string' ? formik.errors.examType : 'Invalid exam type selection'}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Subjects</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {SUBJECTS.map(subject => (
                        <label key={subject} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formik.values.subjects.includes(subject)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const current = formik.values.subjects;
                              const next = checked ? [...current, subject] : current.filter(s => s !== subject);
                              formik.setFieldValue('subjects', next);
                            }}
                          />
                          <span>{subject}</span>
                        </label>
                      ))}
                    </div>
                    {formik.touched.subjects && formik.errors.subjects && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {typeof formik.errors.subjects === 'string' ? formik.errors.subjects : 'Invalid subject selection'}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Teacher Fields */}
              {formik.values.role === 'teacher' && (
                <div className="space-y-4">
                  <Input
                    label="Qualifications (comma-separated)"
                    name="qualifications"
                    type="text"
                    placeholder="e.g. B.Ed, M.Sc"
                    value={formik.values.qualifications}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.qualifications && formik.errors.qualifications}
                  />

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Specialization</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {SUBJECTS.map(subject => (
                        <label key={subject} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formik.values.specialization.includes(subject)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const current = formik.values.specialization;
                              const next = checked ? [...current, subject] : current.filter(s => s !== subject);
                              formik.setFieldValue('specialization', next);
                            }}
                          />
                          <span>{subject}</span>
                        </label>
                      ))}
                    </div>
                    {formik.touched.specialization && formik.errors.specialization && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {typeof formik.errors.specialization === 'string' ? formik.errors.specialization : 'Invalid specialization selection'}
                      </p>
                    )}
                  </div>

                  <Input
                    label="Years of Experience"
                    name="experience"
                    type="number"
                    placeholder="e.g. 3"
                    value={formik.values.experience}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.experience && formik.errors.experience}
                  />
                </div>
              )}

              {/* Email */}
              <Input
                label="Email Address (Optional if phone provided)"
                name="email"
                type="email"
                placeholder="Enter your email"
                icon={Mail}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && formik.errors.email}
                autoComplete="email"
              />

              {/* Phone */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Phone Number (Optional if email provided)</label>
                <PhoneInput
                  value={formik.values.phone}
                  onChange={(v) => formik.setFieldValue('phone', v)}
                  onBlur={() => formik.setFieldTouched('phone', true)}
                  placeholder="e.g. 8012345678"
                  className={cn(
                    "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors",
                    formik.touched.phone && formik.errors.phone && "border-red-500 focus:ring-red-500 focus:border-red-500"
                  )}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <p className="text-sm text-red-600">{formik.errors.phone}</p>
                )}
              </div>

              {/* Role Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">I am a</label>
                <select
                  name="role"
                  value={formik.values.role}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={cn(
                    "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors",
                    formik.touched.role && formik.errors.role && "border-red-500 focus:ring-red-500 focus:border-red-500"
                  )}
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {formik.touched.role && formik.errors.role && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {formik.errors.role}
                  </p>
                )}
              </div>

              {/* Referral Code */}
              <Input
                label="Referral Code (Optional)"
                name="referralCode"
                type="text"
                placeholder="Enter referral code if you have one"
                icon={Gift}
                value={formik.values.referralCode}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.referralCode && formik.errors.referralCode}
                autoComplete="off"
              />
              {formik.values.referralCode && (
                <div className="mt-2">
                  {referralValidationStatus === 'validating' && (
                    <p className="text-sm text-gray-500">Checking referral code...</p>
                  )}
                  {referralValidationStatus === 'valid' && referrerInfo && (
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Referred by: <span className="font-medium">{referrerInfo.name}</span>
                    </p>
                  )}
                  {referralValidationStatus === 'invalid' && referralError && (
                    <p className="text-sm text-red-600 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {referralError}
                    </p>
                  )}
                </div>
              )}

              {/* Password */}
              <div className="space-y-2">
                <Input
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  icon={Lock}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && formik.errors.password}
                  autoComplete="new-password"
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  }
                />
                
                {/* Password Strength Indicator */}
                {formik.values.password && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={cn("h-2 rounded-full transition-all duration-300", passwordStrength.color)}
                          style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-600">
                        {passwordStrength.label}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className={cn("flex items-center gap-1", formik.values.password.length >= 8 ? "text-green-600" : "text-gray-400")}>
                        <CheckCircle2 className="w-3 h-3" />
                        8+ characters
                      </div>
                      <div className={cn("flex items-center gap-1", /[A-Z]/.test(formik.values.password) ? "text-green-600" : "text-gray-400")}>
                        <CheckCircle2 className="w-3 h-3" />
                        Uppercase letter
                      </div>
                      <div className={cn("flex items-center gap-1", /[a-z]/.test(formik.values.password) ? "text-green-600" : "text-gray-400")}>
                        <CheckCircle2 className="w-3 h-3" />
                        Lowercase letter
                      </div>
                      <div className={cn("flex items-center gap-1", /\d/.test(formik.values.password) ? "text-green-600" : "text-gray-400")}>
                        <CheckCircle2 className="w-3 h-3" />
                        Number
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <Input
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your password"
                icon={Lock}
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && formik.errors.confirmPassword}
                autoComplete="new-password"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                }
              />

              {/* Terms Agreement */}
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formik.values.agreeToTerms}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={cn(
                      "mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500",
                      formik.touched.agreeToTerms && formik.errors.agreeToTerms && "border-red-500"
                    )}
                  />
                  <span className="text-sm text-gray-600">
                    I agree to the{' '}
                    <Link to="/terms" target="_blank" className="text-primary-600 hover:text-primary-700 font-medium">
                      Terms of Service
                    </Link>
                    {' '}and{' '}
                    <Link to="/privacy" target="_blank" className="text-primary-600 hover:text-primary-700 font-medium">
                      Privacy Policy
                    </Link>
                  </span>
                </label>
                {formik.touched.agreeToTerms && formik.errors.agreeToTerms && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {formik.errors.agreeToTerms}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={loading || !formik.isValid}
                loading={loading}
                className="w-full"
              >
                <UserPlus className="w-5 h-5" />
                Create Account
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;